import 'react-datepicker/dist/react-datepicker.css';

import { Warning } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import * as _ from 'lodash';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AccessLevelType, type AccessSchedule } from '@/models/channel';
import { applicationDataSelector } from '@/store/slices/application-data';
import { accessUtils } from '@/utils/accessUtils';
import { dateUtils } from '@/utils/dateUtils';

import VNSelect from '../VNSelect/VNSelect';
import VNSortable from '../VNSortable/VNSortable';
import AccessScheduleFormControls, { type ScheduleFormErrors } from './AccessScheduleFormControls';
import VNAccessScheduleCard from './VNAccessScheduleCard';

type Props = {
  name?: string;
  embargoDateField?: string;
  expireDateField?: string;
};

type ScheduleFormValues = {
  from: string | null;
  fromDate: Date | null;
  to: string | null;
  toDate: Date | null;
  accessLevel: string;
};

const emptySchedule = (): ScheduleFormValues => ({
  from: null,
  fromDate: null,
  to: null,
  toDate: null,
  accessLevel: '',
});

const VNAccessSettings = ({ name = '', embargoDateField, expireDateField }: Props) => {
  const resolvedEmbargoDateField = embargoDateField ?? (name === 'accessSettings' ? 'embargoDate' : undefined);
  const resolvedExpireDateField = expireDateField ?? (name === 'accessSettings' ? 'expireDate' : undefined);

  const { control, getValues, setValue, watch } = useFormContext();

  const { ouData } = useSelector(applicationDataSelector);
  const [showAddRow, setShowAddRow] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(getValues(`${name}.effectiveAccessLevel`));
  const [originalAccessLevel] = useState(getValues(`${name}.effectiveAccessLevel`));

  const { fields } = useFieldArray({
    control,
    name: `${name}.temporal.accessSchedules`,
  });

  // Watches
  const accessLevel = watch(`${name}.accessLevel`);

  useEffect(() => {
    recalculateEffectiveAccessStatus();
  }, [accessLevel]);

  // console.log(watch(`${name}.effectiveAccessLevel`));

  const recalculateEffectiveAccessStatus = _.throttle(() => {
    // console.log('recalculateEffectiveAccessStatus', {});
    let newAccessLevel = _.cloneDeep(getValues(`${name}.accessLevel`));
    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`);
    if (accessSchedules != null && accessSchedules.length > 0) {
      const activeAccessSchedules: AccessSchedule[] = [];
      const now = dateUtils.now();
      for (let i = 0; i < accessSchedules.length; i += 1) {
        const sch = accessSchedules[i];
        sch.isActive = false;
        sch.isControlling = false;
        if (dateUtils.isBetween(now, sch.from, sch.to)) {
          sch.isActive = true;
          activeAccessSchedules.push(sch);
        }
      }
      // aalt 2025-06-20 #3289 the most restrictive access schedule always controls
      // ie. If there is a Private access schedule in the current window, it controls
      if (activeAccessSchedules != null && activeAccessSchedules.length > 0) {
        let controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Private);
        if (controllingIndex > -1) {
          activeAccessSchedules[controllingIndex].isControlling = true;
          newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
        } else {
          controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Paywall);
          if (controllingIndex > -1) {
            activeAccessSchedules[controllingIndex].isControlling = true;
            newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
          } else {
            controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Free);
            if (controllingIndex > -1) {
              activeAccessSchedules[controllingIndex].isControlling = true;
              newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
            }
          }
        }
      }
      setValue(`${name}.temporal.accessSchedules`, accessSchedules);
    }
    setEffectiveAccessLevel(newAccessLevel);
    setValue(`${name}.effectiveAccessLevel`, newAccessLevel);
  }, 1000);

  const [newSchedule, setNewSchedule] = useState(emptySchedule);
  const [editSchedule, setEditSchedule] = useState(emptySchedule);
  const [newScheduleErrors, setNewScheduleErrors] = useState<ScheduleFormErrors>({});
  const [editScheduleErrors, setEditScheduleErrors] = useState<ScheduleFormErrors>({});

  const createScheduleChangeHandler =
    (setSchedule: Dispatch<SetStateAction<ScheduleFormValues>>, setFieldErrors: Dispatch<SetStateAction<ScheduleFormErrors>>) =>
      (field: string, value: string | Date) => {
        setSchedule((prev) => ({ ...prev, [field]: value }));
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field as keyof ScheduleFormErrors];
          return next;
        });
      };

  const createScheduleDateChangeHandler =
    (setSchedule: Dispatch<SetStateAction<ScheduleFormValues>>, setFieldErrors: Dispatch<SetStateAction<ScheduleFormErrors>>) =>
      (field: string, value: string | Date) => {
        const newDate = dateUtils.toString(value as Date, ouData.timezone);
        if (field === 'from') {
          setSchedule((prev) => ({ ...prev, fromDate: value as Date, from: newDate }));
        }
        if (field === 'to') {
          setSchedule((prev) => ({ ...prev, toDate: value as Date, to: newDate }));
        }
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field as keyof ScheduleFormErrors];
          return next;
        });
      };

  const handleNewScheduleChange = createScheduleChangeHandler(setNewSchedule, setNewScheduleErrors);
  const handleNewScheduleDateChange = createScheduleDateChangeHandler(setNewSchedule, setNewScheduleErrors);
  const handleEditScheduleChange = createScheduleChangeHandler(setEditSchedule, setEditScheduleErrors);
  const handleEditScheduleDateChange = createScheduleDateChangeHandler(setEditSchedule, setEditScheduleErrors);

  const loadScheduleIntoForm = (schedule: AccessSchedule, setSchedule: Dispatch<SetStateAction<ScheduleFormValues>>) => {
    setSchedule({
      from: schedule.from ?? null,
      fromDate: schedule.from ? dateUtils.toJSDate(schedule.from, ouData.timezone) : null,
      to: schedule.to ?? null,
      toDate: schedule.to ? dateUtils.toJSDate(schedule.to, ouData.timezone) : null,
      accessLevel: schedule.accessLevel ?? '',
    });
  };

  const buildSchedulePayload = (schedule: ScheduleFormValues): AccessSchedule => ({
    from: schedule.from,
    to: schedule.to,
    accessLevel: schedule.accessLevel as AccessLevelType,
  });

  const validateScheduleForm = (
    schedule: ScheduleFormValues,
    accessSchedules: AccessSchedule[],
    replaceIndex?: number
  ): ScheduleFormErrors => {
    const fieldErrors: ScheduleFormErrors = {};
    if (!schedule.from && !schedule.to) {
      fieldErrors.from = 'From or To Date is required';
    }
    if (!schedule.accessLevel) {
      fieldErrors.accessLevel = 'Access Level is required';
    }
    if (schedule.from && schedule.to) {
      const fromDate = new Date(schedule.from);
      const toDate = new Date(schedule.to);
      const minAllowedToDate = new Date(fromDate.getTime() + 24 * 60 * 60 * 1000);
      if (toDate < minAllowedToDate) {
        fieldErrors.to = 'To Date must be at least 24 hours after From Date';
      }
    }

    const overlapError = accessUtils.validateScheduleOverlap(accessSchedules, buildSchedulePayload(schedule), replaceIndex);
    if (overlapError) {
      fieldErrors.from = overlapError;
    }

    return fieldErrors;
  };

  const applySchedulesAndSync = (accessSchedules: AccessSchedule[]) => {
    setValue(`${name}.temporal.accessSchedules`, accessSchedules);
    recalculateEffectiveAccessStatus();
    accessUtils.syncEmbargoExpireFromSchedules(accessSchedules, setValue, resolvedEmbargoDateField, resolvedExpireDateField);
  };

  const handleScheduleDeleted = () => {
    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`) ?? [];
    applySchedulesAndSync(accessSchedules);
  };

  const handleEditSchedule = (index: number) => {
    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`) ?? [];
    loadScheduleIntoForm(accessSchedules[index], setEditSchedule);
    setEditScheduleErrors({});
    setEditingIndex(index);
  };

  const addScheduleClick = () => {
    setNewSchedule(emptySchedule());
    setNewScheduleErrors({});
    setShowAddRow(true);
  };

  const cancelAddSchedule = () => {
    setNewSchedule(emptySchedule());
    setNewScheduleErrors({});
    setShowAddRow(false);
  };

  const cancelEditSchedule = () => {
    setEditSchedule(emptySchedule());
    setEditScheduleErrors({});
    setEditingIndex(null);
  };

  const saveAddSchedule = () => {
    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`) ?? [];
    const fieldErrors = validateScheduleForm(newSchedule, accessSchedules);
    if (Object.keys(fieldErrors).length > 0) {
      setNewScheduleErrors(fieldErrors);
      return;
    }

    const schedulePayload = buildSchedulePayload(newSchedule);
    accessSchedules.push(schedulePayload);

    applySchedulesAndSync(accessSchedules);
    setNewSchedule(emptySchedule());
    setNewScheduleErrors({});
    setShowAddRow(false);
  };

  const saveEditSchedule = () => {
    if (editingIndex == null) {
      return;
    }

    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`) ?? [];
    const fieldErrors = validateScheduleForm(editSchedule, accessSchedules, editingIndex);
    if (Object.keys(fieldErrors).length > 0) {
      setEditScheduleErrors(fieldErrors);
      return;
    }

    const schedulePayload = buildSchedulePayload(editSchedule);
    accessSchedules[editingIndex] = schedulePayload;

    applySchedulesAndSync(accessSchedules);
    setEditSchedule(emptySchedule());
    setEditScheduleErrors({});
    setEditingIndex(null);
  };

  return (
    <div className="vn-access-settings">
      <Box display="flex" gap={1} justifyContent="space-between" width="100%">
        {/* <span>{JSON.stringify(getValues(name))}</span> */}
        <VNSelect name={`${name}.accessLevel`} label="Default Access" options={ouData.metaData.defaultAccessLevelOptions} />
        {/* {statusFieldName && ouData && ouData.metaData.enablePaywall && <StatusDropdown name={statusFieldName} />} */}
      </Box>

      <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
        <h2 style={{ fontWeight: 'bold', position: 'relative', left: '4px' }}>Access Schedules:</h2>
      </Box>

      {/* Render Added Schedules */}
      <Box mt={2}>
        {fields.length === 0 && (
          <Typography color="text.secondary" sx={{ marginLeft: '32px' }}>
            No schedules added.
          </Typography>
        )}
        {fields.length > 0 && (
          <div className="sortable-portlets access-schedule-sortable">
            <VNSortable
              name={`${name}.temporal.accessSchedules`}
              deletable
              itemTemplate={VNAccessScheduleCard}
              itemTemplateProps={{
                editingIndex,
                scheduleForm: editSchedule,
                fieldErrors: editScheduleErrors,
                onScheduleFieldChange: handleEditScheduleChange,
                onScheduleDateChange: handleEditScheduleDateChange,
                onCancelScheduleEdit: cancelEditSchedule,
                onSaveSchedule: saveEditSchedule,
              }}
              onDeleted={handleScheduleDeleted}
              onSorted={recalculateEffectiveAccessStatus}
              onEdit={handleEditSchedule}
              editingIndex={editingIndex}
            />
          </div>
        )}
      </Box>

      {!showAddRow && (
        <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
          <Button
            sx={{ whiteSpace: 'nowrap', height: '37px', width: '200px' }}
            variant="contained"
            color="primary"
            onClick={addScheduleClick}
          >
            ADD SCHEDULE
          </Button>
        </Box>
      )}

      {showAddRow && (
        <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
          <AccessScheduleFormControls
            schedule={newSchedule}
            ouData={ouData}
            fieldErrors={newScheduleErrors}
            onFieldChange={handleNewScheduleChange}
            onDateChange={handleNewScheduleDateChange}
            onCancel={cancelAddSchedule}
            onSave={saveAddSchedule}
          />
        </Box>
      )}

      <Divider style={{ marginTop: '24px' }} />
      <Box mt={3} mb={3} display="flex" gap={1} justifyContent="space-between" width="100%">
        <div>
          {effectiveAccessLevel !== originalAccessLevel && <Warning className="status-warning" />}
          <div style={{ display: 'inline-block', position: 'relative', paddingLeft: '12px', lineHeight: '18px' }}>
            Current Access Level: <span style={{ fontWeight: 'bold' }}>{originalAccessLevel || 'Public'}</span>
            <br />
            {effectiveAccessLevel !== originalAccessLevel && (
              <span>
                On Save, current access level will be: <span style={{ fontWeight: 'bold' }}>{effectiveAccessLevel}.</span>
              </span>
            )}
          </div>
        </div>
      </Box>

      <Typography className="access-schedules-timezone-footer">
        All times are in {ouData.timezoneAbbreviation} timezone.
      </Typography>
    </div>
  );
};

export default VNAccessSettings;
